import React, { useState, useEffect } from "react";
import { productionAPI, usersAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import {
  ChefHat, ClipboardList, Trash2, Plus, Check, RefreshCw,
  AlertTriangle, Download, Loader, CheckCircle, Play, Calendar,
  Printer, BookOpen, Map
} from "lucide-react";
import { exportCSV } from "../utils/csvUtils";
import { useConfirm } from "../hooks/useConfirm";
import * as XLSX from "xlsx";

const MEAL_TYPES = ["breakfast", "lunch", "snack"];
const WASTE_REASONS = ["overproduction", "spoilage", "spillage", "returned_from_schools", "quality_rejected", "other"];

const TABS = [
  { id: "cooksheet", label: "Daily Cook Sheet", icon: ChefHat },
  { id: "waste", label: "Waste Log", icon: Trash2 },
];

export default function ProductionPlanning() {
  const { selectedKitchen, kitchens: ctxKitchens } = useKitchen();
  const [kitchens, setKitchens] = useState(ctxKitchens || []);
  const { confirm, ConfirmModal } = useConfirm();
  const [tab, setTab] = useState("cooksheet");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [cookSheet, setCookSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  // Waste log state
  const [wasteLogs, setWasteLogs] = useState([]);
  const [wasteLoading, setWasteLoading] = useState(false);
  const [wasteForm, setWasteForm] = useState({ date, meal_type: "MDM", items: [], notes: "" });
  const [wasteItem, setWasteItem] = useState({ item_name: "", planned_qty: "", actual_used_qty: "", waste_qty: "", waste_reason: "overproduction" });
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (ctxKitchens?.length > 0) { setKitchens(ctxKitchens); return; }
    usersAPI.getKitchens().then(r => setKitchens(r.data || [])).catch(() => {});
  }, [ctxKitchens]);

  useEffect(() => { fetchCookSheet(); }, [date, selectedKitchen]);
  useEffect(() => { if (tab === "waste") { fetchWaste(); } }, [tab, selectedKitchen]);

  const fetchCookSheet = async () => {
    setLoading(true); setError("");
    try {
      const r = await productionAPI.getCookSheet({ date, kitchen_id: selectedKitchen || null });
      setCookSheet(r.data);
      setNotes(r.data?.production_order?.notes || "");
    } catch (e) { setError(formatError(e)); }
    setLoading(false);
  };

  const fetchWaste = async () => {
    setWasteLoading(true);
    try {
      const r = await productionAPI.getWaste({ kitchen_id: selectedKitchen || null, limit: 100 });
      setWasteLogs(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setWasteLoading(false);
  };

  const handleConfirmCookSheet = async () => {
    if (!cookSheet) return;
    setSaving(true); setMsg("");
    try {
      await productionAPI.confirmCookSheet({ ...cookSheet, notes, kitchen_id: selectedKitchen || null });
      setMsg("Production order confirmed!");
      fetchCookSheet();
    } catch (e) { setError(formatError(e)); }
    setSaving(false);
  };

  const handleStatusUpdate = async (status) => {
    if (!cookSheet?.production_order?.id) return;
    await productionAPI.updateStatus(cookSheet.production_order.id, { status });
    fetchCookSheet();
  };

  const addWasteItem = () => {
    if (!wasteItem.item_name) return;
    setWasteForm(f => ({ ...f, items: [...f.items, { ...wasteItem }] }));
    setWasteItem({ item_name: "", planned_qty: "", actual_used_qty: "", waste_qty: "", waste_reason: "overproduction" });
  };

  const handleSaveWaste = async () => {
    if (wasteForm.items.length === 0) { setError("Add at least one item"); return; }
    try {
      await productionAPI.createWaste({ ...wasteForm, kitchen_id: selectedKitchen || null });
      setMsg("Waste log saved!");
      setWasteForm({ date, meal_type: "MDM", items: [], notes: "" });
      fetchWaste();
    } catch (e) { setError(formatError(e)); }
  };

  const handleDeleteWaste = async (id) => {
    if (!await confirm("Delete this waste log entry?")) return;
    await productionAPI.deleteWaste(id);
    fetchWaste();
  };

  const exportIngredients = () => {
    if (!cookSheet?.ingredients) return;
    exportCSV(cookSheet.ingredients.map(i => ({
      ingredient: i.item_name,
      quantity: i.total_quantity,
      unit: i.unit,
      for_meals: (i.for_meals || []).join(", "),
    })), `cook_sheet_${date}`);
  };

  const exportExcel = () => {
    if (!cookSheet) return;
    const wb = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = [
      ["MANNA TRUST — DAILY COOK SHEET"],
      [`Date: ${date}`, `Kitchen: ${kitchenName}`, `Generated: ${new Date().toLocaleString("en-IN")}`],
      [],
      ["MDM Students", cookSheet.mdm_students],
      ["Breakfast Students", cookSheet.bf_students],
      ["Egg Students", cookSheet.egg_students],
      ["Total Students (MDM+BF)", cookSheet.total_students],
      ["Schools", cookSheet.school_count],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

    // Sheet 2: Consolidated Ingredients
    const ingRows = [
      ["Ingredient", "Total Quantity", "Unit", "For Meals"],
      ...(cookSheet.ingredients || []).map(i => [i.item_name, i.total_quantity, i.unit, [...new Set(i.for_meals || [])].join(", ")])
    ];
    const wsIng = XLSX.utils.aoa_to_sheet(ingRows);
    XLSX.utils.book_append_sheet(wb, wsIng, "Ingredients");

    // Sheet 3: Per-Recipe Breakdown
    if (cookSheet.recipe_breakdown?.length > 0) {
      const rbRows = [["Menu Item", "Meal Type", "Students", "Ingredient", "Qty/Serving", "Total Qty", "Unit"]];
      for (const r of cookSheet.recipe_breakdown) {
        if (r.ingredients?.length > 0) {
          r.ingredients.forEach((ing, idx) => {
            rbRows.push([idx === 0 ? r.menu_item : "", idx === 0 ? r.meal_type : "", idx === 0 ? r.students : "", ing.item_name, ing.qty_per_serving, ing.total_qty, ing.unit]);
          });
        } else {
          rbRows.push([r.menu_item, r.meal_type, r.students, "—", "—", "—", "—"]);
        }
      }
      const wsRb = XLSX.utils.aoa_to_sheet(rbRows);
      XLSX.utils.book_append_sheet(wb, wsRb, "Recipe Breakdown");
    }

    // Sheet 4: Route Summary
    if (cookSheet.route_summary?.length > 0) {
      const rsRows = [["Route No.", "MDM Students", "Breakfast Students", "Egg Students", "Schools"],
        ...cookSheet.route_summary.map(r => [r.route_number, r.mdm, r.breakfast, r.egg, r.school_count])
      ];
      const wsRs = XLSX.utils.aoa_to_sheet(rsRows);
      XLSX.utils.book_append_sheet(wb, wsRs, "Route Summary");
    }

    XLSX.writeFile(wb, `CookSheet_${date}.xlsx`);
  };

  const kitchenName = kitchens.find(k => k.id === selectedKitchen)?.name || "All Kitchens";

  return (
    <div className="space-y-5 max-w-5xl" data-testid="production-planning">
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C] flex items-center gap-3">
          <span className="w-10 h-10 bg-[#E8956D]/15 rounded-xl flex items-center justify-center">
            <ChefHat size={20} className="text-[#E8956D]" />
          </span>
          Production Planning
        </h1>
        <p className="text-sm text-[#8A7E73] mt-0.5 ml-14">Daily cook sheet from school strength × menu plan × recipes</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#E5E1DB]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-2 -mb-px transition-colors ${tab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {error && <div className="bg-[#FBEAE7] text-[#C2624E] rounded-xl px-4 py-3 text-sm">{error}</div>}
      {msg && <div className="bg-[#EDF2EE] text-[#637C64] rounded-xl px-4 py-3 text-sm flex items-center gap-2"><CheckCircle size={14}/>{msg}</div>}

      {/* ── COOK SHEET TAB ── */}
      {tab === "cooksheet" && (
        <div className="space-y-4">
          {/* Controls */}
          <div className="flex gap-3 items-end flex-wrap">
            <div>
              <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
            </div>
            <button onClick={() => setDate(new Date().toISOString().split("T")[0])}
              className="flex items-center gap-1.5 border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
              <Calendar size={13} /> Today
            </button>
            <button onClick={() => { const d = new Date(); d.setDate(d.getDate() + 1); setDate(d.toISOString().split("T")[0]); }}
              className="flex items-center gap-1.5 bg-[#E8956D]/10 border border-[#E8956D]/30 rounded-lg px-3 py-2 text-xs font-bold text-[#E8956D] hover:bg-[#E8956D]/20">
              <Calendar size={13} /> Tomorrow
            </button>
            <button onClick={fetchCookSheet} disabled={loading}
              className="flex items-center gap-2 border border-[#E5E1DB] rounded-lg px-4 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
              {loading ? <Loader size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
            </button>
            {cookSheet?.ingredients?.length > 0 && (
              <div className="flex gap-2 ml-auto">
                <button onClick={exportIngredients} className="flex items-center gap-1.5 border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0]">
                  <Download size={14} /> CSV
                </button>
                <button onClick={exportExcel} className="flex items-center gap-1.5 bg-[#637C64] text-white rounded-lg px-3 py-2 text-sm font-bold hover:bg-[#4F6450]">
                  <Download size={14} /> Excel
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader size={28} className="animate-spin text-[#E8956D]" /></div>
          ) : cookSheet && (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">MDM Students</p>
                  <p className="text-2xl font-extrabold text-green-700">{cookSheet.mdm_students.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Breakfast</p>
                  <p className="text-2xl font-extrabold text-amber-600">{cookSheet.bf_students.toLocaleString()}</p>
                </div>
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Total Students</p>
                  <p className="text-2xl font-extrabold text-[#E8956D]">{cookSheet.total_students.toLocaleString()}</p>
                  <p className="text-[10px] text-[#8A7E73] mt-0.5">MDM + Breakfast</p>
                </div>
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Egg Supply</p>
                  <p className="text-2xl font-extrabold text-purple-700">
                    {(cookSheet.egg_students || 0).toLocaleString()}
                  </p>
                  <p className="text-[10px] text-[#8A7E73] mt-0.5">Counted separately</p>
                </div>
                <div className="bg-white border border-[#E5E1DB] rounded-xl p-4">
                  <p className="text-xs text-[#8A7E73] uppercase font-bold mb-1">Schools</p>
                  <p className="text-2xl font-extrabold text-[#3A322C]">{cookSheet.school_count}</p>
                </div>
              </div>

              {/* Warnings */}
              {!cookSheet.has_menu_plan && (
                <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-[#D98032]">
                  <AlertTriangle size={16} /> No menu plan for {date}. Go to <strong>Kitchen & Menu → Daily Plan</strong> to add one. Ingredient quantities won't be available until then.
                </div>
              )}
              {cookSheet.total_students === 0 && (
                <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-[#D98032]">
                  <AlertTriangle size={16} /> No school strength entered for {date}. Go to <strong>School Indent</strong> to enter today's strength.
                </div>
              )}

              {/* Production order status banner */}
              {cookSheet.production_order && (
                <div className={`rounded-xl px-4 py-3 flex items-center justify-between gap-3 border ${cookSheet.production_order.status === "completed" ? "bg-[#EDF2EE] border-[#637C64]/30" : cookSheet.production_order.status === "in_progress" ? "bg-blue-50 border-blue-200" : "bg-[#FDF4EB] border-[#D98032]/30"}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">Production Order: </span>
                    <span className={`text-sm font-extrabold capitalize ${cookSheet.production_order.status === "completed" ? "text-[#637C64]" : "text-[#D98032]"}`}>
                      {cookSheet.production_order.status}
                    </span>
                    <span className="text-xs text-[#8A7E73]">· Confirmed by {cookSheet.production_order.confirmed_by}</span>
                  </div>
                  <div className="flex gap-2">
                    {cookSheet.production_order.status === "planned" && (
                      <button onClick={() => handleStatusUpdate("in_progress")} className="flex items-center gap-1 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg font-bold">
                        <Play size={11} /> Start Cooking
                      </button>
                    )}
                    {cookSheet.production_order.status === "in_progress" && (
                      <button onClick={() => handleStatusUpdate("completed")} className="flex items-center gap-1 text-xs bg-[#637C64] text-white px-3 py-1.5 rounded-lg font-bold">
                        <Check size={11} /> Mark Complete
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Menu summary */}
              {cookSheet.menu_summary?.length > 0 && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                    <h3 className="font-bold text-[#3A322C] text-sm">Today's Menu</h3>
                  </div>
                  <div className="divide-y divide-[#F4F3F0]">
                    {cookSheet.menu_summary.map((m, i) => (
                      <div key={`idx-${i}`} className="px-5 py-3 flex items-center gap-3">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${m.meal_type === "breakfast" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                          {m.meal_type}
                        </span>
                        <span className="font-medium text-[#3A322C] text-sm">{m.menu_item}</span>
                        <span className="text-xs text-[#8A7E73] ml-auto">{m.planned_servings} servings</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Ingredient requirements */}
              <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
                  <h3 className="font-bold text-[#3A322C] text-sm flex items-center gap-2"><BookOpen size={15} className="text-[#E8956D]" />Ingredients Required</h3>
                  <span className="text-xs text-[#8A7E73]">{cookSheet.ingredients?.length || 0} items</span>
                </div>
                {cookSheet.ingredients?.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-[#F8F8F6]">
                      <tr>{["Ingredient", "Quantity", "Unit", "For Meals"].map(h => (
                        <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-[#F4F3F0]">
                      {cookSheet.ingredients.map((ing, i) => (
                        <tr key={`idx-${i}`} className="hover:bg-[#FAFAF8]">
                          <td className="px-4 py-3 font-medium text-[#3A322C]">{ing.item_name}</td>
                          <td className="px-4 py-3 font-bold text-[#E8956D]">{ing.total_quantity}</td>
                          <td className="px-4 py-3 text-[#5C5147]">{ing.unit}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {[...new Set(ing.for_meals || [])].map(m => (
                                <span key={m} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${m === "breakfast" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{m}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-center py-8 text-[#8A7E73] text-sm">
                    {cookSheet.has_menu_plan ? "Add recipes to menu items to see ingredient requirements" : "Add a menu plan for today to generate the cook sheet"}
                  </p>
                )}
              </div>

              {/* Per-recipe breakdown */}
              {cookSheet.recipe_breakdown?.length > 0 && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                    <h3 className="font-bold text-[#3A322C] text-sm flex items-center gap-2"><ChefHat size={15} className="text-[#E8956D]" />Per-Recipe Breakdown</h3>
                  </div>
                  <div className="divide-y divide-[#F4F3F0]">
                    {cookSheet.recipe_breakdown.map((rb, ri) => (
                      <div key={ri} className="p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${rb.meal_type === "breakfast" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{rb.meal_type}</span>
                          <span className="font-bold text-[#3A322C] text-sm">{rb.menu_item}</span>
                          <span className="text-xs text-[#8A7E73] ml-auto">{rb.students.toLocaleString()} students</span>
                        </div>
                        {rb.ingredients?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-[#F8F8F6]">
                                  {["Ingredient", "Per Serving", "Total Qty", "Unit"].map(h => (
                                    <th key={h} className="text-left px-3 py-2 font-bold text-[#8A7E73] uppercase tracking-wide">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-[#F4F3F0]">
                                {rb.ingredients.map((ing, ii) => (
                                  <tr key={ii} className="hover:bg-[#FAFAF8]">
                                    <td className="px-3 py-2 font-medium text-[#3A322C]">{ing.item_name}</td>
                                    <td className="px-3 py-2 text-[#8A7E73]">{ing.qty_per_serving}</td>
                                    <td className="px-3 py-2 font-bold text-[#E8956D]">{ing.total_qty}</td>
                                    <td className="px-3 py-2 text-[#5C5147]">{ing.unit}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-xs text-[#8A7E73] italic">No recipe defined for this item</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Route-wise strength summary */}
              {cookSheet.route_summary?.length > 0 && (
                <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
                  <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB]">
                    <h3 className="font-bold text-[#3A322C] text-sm flex items-center gap-2"><Map size={15} className="text-[#E8956D]" />Route-wise Strength</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-[#F8F8F6]">
                        <tr>{["Route", "MDM", "Breakfast", "Egg", "Schools"].map(h => (
                          <th key={h} className="text-left px-4 py-2.5 text-xs font-bold uppercase text-[#5C5147]">{h}</th>
                        ))}</tr>
                      </thead>
                      <tbody className="divide-y divide-[#F4F3F0]">
                        {cookSheet.route_summary.map((r, i) => (
                          <tr key={`idx-${i}`} className="hover:bg-[#FAFAF8]">
                            <td className="px-4 py-2.5 font-bold text-[#E8956D]">Route {r.route_number}</td>
                            <td className="px-4 py-2.5 text-[#637C64] font-bold">{r.mdm.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-amber-600 font-bold">{r.breakfast.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-purple-600 font-bold">{r.egg.toLocaleString()}</td>
                            <td className="px-4 py-2.5 text-[#5C5147]">{r.school_count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Confirm */}
              <div className="bg-white border border-[#E5E1DB] rounded-xl p-4 space-y-3">
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Notes for Kitchen Team</label>
                <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Any special instructions for today's cooking..." 
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" />
                <button onClick={handleConfirmCookSheet} disabled={saving || cookSheet.total_students === 0}
                  className="w-full bg-[#E8956D] text-[#3A322C] rounded-xl py-3 font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#D48661] disabled:opacity-60">
                  {saving ? <Loader size={15} className="animate-spin" /> : <ClipboardList size={15} />}
                  {cookSheet.production_order ? "Update Production Order" : "Confirm Production Order"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── WASTE LOG TAB ── */}
      {tab === "waste" && (
        <div className="space-y-4">
          {/* Entry form */}
          <div className="bg-white border border-[#E5E1DB] rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-[#3A322C]">Log Today's Waste / Leftover</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
                <input type="date" value={wasteForm.date} onChange={e => setWasteForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Meal Type</label>
                <select value={wasteForm.meal_type} onChange={e => setWasteForm(f => ({ ...f, meal_type: e.target.value }))}
                  className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
                  <option value="MDM">MDM (Lunch)</option>
                  <option value="breakfast">Breakfast</option>
                </select>
              </div>
            </div>

            {/* Add waste items */}
            <div className="bg-[#F4F3F0] rounded-xl p-3 space-y-2">
              <p className="text-xs font-bold uppercase text-[#8A7E73]">Add Item</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <input placeholder="Item name" value={wasteItem.item_name} onChange={e => setWasteItem(w => ({ ...w, item_name: e.target.value }))}
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs outline-none focus:border-[#E8956D] col-span-2 md:col-span-1" />
                <input type="number" placeholder="Planned (kg)" value={wasteItem.planned_qty} onChange={e => setWasteItem(w => ({ ...w, planned_qty: e.target.value }))}
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs outline-none focus:border-[#E8956D]" />
                <input type="number" placeholder="Used (kg)" value={wasteItem.actual_used_qty} onChange={e => setWasteItem(w => ({ ...w, actual_used_qty: e.target.value }))}
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs outline-none focus:border-[#E8956D]" />
                <input type="number" placeholder="Wasted (kg)" value={wasteItem.waste_qty} onChange={e => setWasteItem(w => ({ ...w, waste_qty: e.target.value }))}
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs outline-none focus:border-[#E8956D]" />
                <select value={wasteItem.waste_reason} onChange={e => setWasteItem(w => ({ ...w, waste_reason: e.target.value }))}
                  className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs outline-none focus:border-[#E8956D]">
                  {WASTE_REASONS.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <button onClick={addWasteItem} className="text-xs bg-[#E8956D] text-[#3A322C] px-4 py-2 rounded-lg font-bold flex items-center gap-1">
                <Plus size={12} /> Add Item
              </button>
            </div>

            {wasteForm.items.length > 0 && (
              <div className="space-y-1">
                {wasteForm.items.map((item, i) => (
                  <div key={`idx-${i}`} className="flex items-center justify-between bg-[#FAFAF8] border border-[#E5E1DB] rounded-lg px-3 py-2 text-xs">
                    <span className="font-bold text-[#3A322C]">{item.item_name}</span>
                    <span className="text-[#8A7E73]">Planned: {item.planned_qty}kg | Used: {item.actual_used_qty}kg</span>
                    <span className="font-bold text-[#C2624E]">Waste: {item.waste_qty}kg</span>
                    <span className="text-[#8A7E73] capitalize">{item.waste_reason?.replace(/_/g," ")}</span>
                    <button onClick={() => setWasteForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }))} className="text-[#C2624E]"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}

            <textarea rows={2} placeholder="Additional notes..." value={wasteForm.notes} onChange={e => setWasteForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D] resize-none" />

            <button onClick={handleSaveWaste} disabled={wasteForm.items.length === 0}
              className="w-full bg-[#637C64] text-white rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              <Check size={15} /> Save Waste Log
            </button>
          </div>

          {/* History */}
          <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
            <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
              <h3 className="font-bold text-[#3A322C] text-sm">Waste Log History</h3>
              {wasteLoading && <Loader size={14} className="animate-spin text-[#E8956D]" />}
            </div>
            {wasteLogs.length === 0 ? (
              <div className="text-center py-10 text-[#8A7E73] text-sm">No waste logs yet</div>
            ) : (
              <div className="divide-y divide-[#F4F3F0]">
                {wasteLogs.map(w => (
                  <div key={w.id} className="px-5 py-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-[#3A322C] text-sm">{w.date}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${w.meal_type === "breakfast" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{w.meal_type}</span>
                        <span className="text-xs font-bold text-[#C2624E]">{w.total_waste_kg} kg wasted</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {(w.items || []).map((item, i) => (
                          <span key={`idx-${i}`} className="text-[10px] bg-[#F4F3F0] text-[#5C5147] px-2 py-0.5 rounded-full">
                            {item.item_name}: {item.waste_qty}kg
                          </span>
                        ))}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteWaste(w.id)} className="text-[#8A7E73] hover:text-[#C2624E] shrink-0"><Trash2 size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {ConfirmModal}
    </div>
  );
}
